import { Link } from "react-router-dom";
import { useEffect } from "react";
import JsonLd from "@/components/base/JsonLd";
import { articlesMeta, buildArticleJsonLd } from "@/mocks/seoMetadata";

const article = articlesMeta.find((a) => a.slug === "/articoli/collaborazione-itaobuy")!;

export default function CollaborazionePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleJsonLd(article)} />
      <header className="w-full px-6 md:px-10 lg:px-16 py-5 bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-2xl font-bold text-accent-500 tracking-tight">ItaoBuy</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-accent-600">
              itaobuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-primary-600">
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
          </div>
        </div>
      </header>

      <article className="w-full px-6 md:px-10 lg:px-16 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-400 whitespace-nowrap">Collaborazione Team ItaoBuy</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">Team Guide</span>
              <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">Collaborazione</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              Collaborazione in Team con ItaoBuy Spreadsheet su Google Sheets
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              Scopri come utilizzare <strong>ItaoBuy Spreadsheet</strong> in modalità collaborativa. Condivisione in tempo reale, commenti, cronologia versioni e gestione dei permessi per team di acquisti di ogni dimensione.
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>Aggiornato: Luglio 2026</span>
              <span>|</span>
              <span>Tempo di lettura: 9 minuti</span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20office%20workspace%20with%20multiple%20laptops%20showing%20shared%20spreadsheet%20on%20screens%2C%20team%20members%20working%20together%20at%20a%20warm%20cream%20colored%20desk%2C%20soft%20natural%20lighting%2C%20professional%20teamwork%20photography%2C%20clean%20harmonious%20composition&width=1200&height=600&seq=article-collab-hero&orientation=landscape" alt="Collaborazione Team ItaoBuy Spreadsheet su Google Sheets" title="Collaborazione ItaoBuy Spreadsheet - Lavoro di Squadra e Condivisione" className="w-full h-auto object-cover" />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Il Potere della Collaborazione su ItaoBuy Spreadsheet</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Quando il lavoro di acquisti coinvolge più persone, la comunicazione diventa il fattore critico di successo. <strong>ItaoBuy Spreadsheet</strong> su Google Sheets offre collaborazione in tempo reale nativa: più utenti possono lavorare contemporaneamente sullo stesso foglio, vedere i cambiamenti istantaneamente e comunicare direttamente all&apos;interno del documento.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Che tu gestisca un team di due persone o un reparto acquisti di venti membri, le funzionalità collaborative di Google Sheets ti permettono di mantenere tutti allineati senza flussi email infiniti o versioni disordinate dello stesso file.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Funzionalità Collaborative Essenziali</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">1</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Condivisione in Tempo Reale</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Ogni modifica apportata al <strong>ItaoBuy Spreadsheet</strong> è visibile istantaneamente a tutti i membri del team. I cursori colorati mostrano chi sta modificando cosa, eliminando conflitti di versione e riducendo al minimo la duplicazione del lavoro.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">2</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Commenti e Menzioni</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Aggiungi commenti direttamente sulle celle per discutere prezzi specifici o stati ordine. Utilizza le menzioni (@nome) per notificare istantaneamente i colleghi. I thread di commento mantengono il contesto senza intasare la casella email del team.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">3</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Cronologia Versioni</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Google Sheets salva automaticamente ogni modifica del tuo <strong>ItaoBuy Spreadsheet</strong>. Puoi vedere chi ha fatto cosa e quando, e ripristinare versioni precedenti in pochi clic. Questa funzionalità elimina il timore di cancellare accidentalmente dati importanti.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">4</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Gestione Permessi Granulare</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Assegna ruoli diversi a membri diversi del team. I buyer possono avere accesso in modifica alle colonne dei prezzi, i manager possono vedere dashboard riassuntivi in sola lettura, e gli amministratori controllano tutto. Ogni foglio del tuo <strong>ItaoBuy Spreadsheet</strong> può avere permessi diversi.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10 p-6 md:p-8 bg-accent-500 rounded-2xl text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Lavora in Team con ItaoBuy Spreadsheet</h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              Inizia subito a utilizzare <strong className="text-white">ItaoBuy Spreadsheet</strong> in modalità collaborativa e trasforma la produttività del tuo team.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-6 py-3 bg-white text-accent-500 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/90">
                Visita itaobuy sheet <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
              </a>
              <Link to="/" className="flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/25">
                Torna alla Home <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              </Link>
            </div>
          </section>

          <nav className="flex items-center justify-between pt-6 border-t border-background-200/70">
            <Link to="/" className="flex items-center gap-2 text-sm text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span> ItaoBuy Spreadsheet Home
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-400">Condividi:</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su X"><i className="ri-twitter-x-line text-sm"></i></a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su Facebook"><i className="ri-facebook-fill text-sm"></i></a>
            </div>
          </nav>
        </div>
      </article>

      <footer className="w-full px-4 md:px-8 pt-4 pb-6 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500">&copy; {new Date().getFullYear()} ItaoBuy Spreadsheet Italia. <Link to="/" className="text-accent-500 hover:text-accent-600 transition-colors cursor-pointer ml-1">Torna alla Home</Link></p>
        </div>
      </footer>
    </main>
  );
}